import createReducer from "./createReducer";
import { SUPPRESS_SUCCESS_GROWL, DEFAULT_RPP } from "utils/constants";

export const LIBRARY_ACTIONS = {
	CLEAR_PROPERTIES_FORM: "CLEAR_PROPERTIES_FORM",

	CREATE_LIBRARY_AJAX: "CREATE_LIBRARY_AJAX",
	CREATE_LIBRARY_SUCCESS: "CREATE_LIBRARY_SUCCESS",
	CREATE_LIBRARY_ERROR: "CREATE_LIBRARY_ERROR",

	UPDATE_LIBRARY_AJAX: "UPDATE_LIBRARY_AJAX",
	UPDATE_LIBRARY_SUCCESS: "UPDATE_LIBRARY_SUCCESS",
	UPDATE_LIBRARY_ERROR: "UPDATE_LIBRARY_ERROR",

	DELETE_LIBRARY_AJAX:    "DELETE_LIBRARY_AJAX",
	DELETE_LIBRARY_SUCCESS: "DELETE_LIBRARY_SUCCESS",
	DELETE_LIBRARY_ERROR:   "DELETE_LIBRARY_ERROR",
	SHARE_LIBRARY_AJAX:     "SHARE_LIBRARY_AJAX",
	SHARE_LIBRARY_SUCCESS:  "SHARE_LIBRARY_SUCCESS",
	SHARE_LIBRARY_ERROR:    "SHARE_LIBRARY_ERROR",

	LOAD_LIBRARY_AJAX: "LOAD_LIBRARY_AJAX",
	LOAD_LIBRARY_SUCCESS: "LOAD_LIBRARY_SUCCESS",
	LOAD_LIBRARY_ERROR: "LOAD_LIBRARY_ERROR",
	LOAD_LIBRARY_CANCELED: "LOAD_LIBRARY_CANCELED",

	LOAD_LIBRARY_ADVERTISERS_AJAX: "LOAD_LIBRARY_ADVERTISERS_AJAX",
	LOAD_LIBRARY_ADVERTISERS_SUCCESS: "LOAD_LIBRARY_ADVERTISERS_SUCCESS",
	LOAD_LIBRARY_ADVERTISERS_ERROR: "LOAD_LIBRARY_ADVERTISERS_ERROR",
};

export function loadLibrary(id, cancelToken){
	return (dispatch, getState) => {
		let currentState = getState().libraryProperties;

		// If we already have campaign loaded, then don't ask for it again
		if(currentState && currentState.id === parseInt(id)){
			return Promise.resolve({ success: true });
		}

		return dispatch({
			type: LIBRARY_ACTIONS.LOAD_LIBRARY_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${id}`,
				success: LIBRARY_ACTIONS.LOAD_LIBRARY_SUCCESS,
				error: LIBRARY_ACTIONS.LOAD_LIBRARY_ERROR,
				cancel: LIBRARY_ACTIONS.LOAD_LIBRARY_CANCELED,
				cancelToken,
			},
		});
	};
}

export function createLibrary(data){
	return (dispatch) => {
		return dispatch({
			type: LIBRARY_ACTIONS.CREATE_LIBRARY_AJAX,
			AJAX: {
				url: "creative-libraries",
				method: "post",
				data,
				success: LIBRARY_ACTIONS.CREATE_LIBRARY_SUCCESS,
				error: LIBRARY_ACTIONS.CREATE_LIBRARY_ERROR,
			},
		});
	};
}

export function updateLibrary(id, data, extraArgs = {}){
	const { meta = { suppressGrowl: false } } = extraArgs;

	return (dispatch) => {
		return dispatch({
			type: LIBRARY_ACTIONS.UPDATE_LIBRARY_AJAX,
			AJAX: {
				url: `creative-libraries/${id}`,
				method: "put",
				data,
				success: LIBRARY_ACTIONS.UPDATE_LIBRARY_SUCCESS,
				error: LIBRARY_ACTIONS.UPDATE_LIBRARY_ERROR,
			},
			meta,
		});
	};
}

export function deleteLibrary(id){
	return (dispatch) => {
		if(!id){ return Promise.reject({ success:false }); }

		return dispatch({
			type: LIBRARY_ACTIONS.DELETE_LIBRARY_AJAX,
			AJAX: {
				url: `creative-libraries/${id}`,
				method: "delete",
				headers:{
					"content-type": "application/json",
				},
				success: LIBRARY_ACTIONS.DELETE_LIBRARY_SUCCESS,
				error: LIBRARY_ACTIONS.DELETE_LIBRARY_ERROR,
			},
			meta: {
				buildMessage: buildLibraryDeleteResultMessage,
			},
			payload: {
				id, // After the middleware, this should be available as `action.payload.original.id` in the handlers.
			},
		});
	};
}

export function shareLibrary(id, withWho){
	return (dispatch) => {
		return dispatch({
			type:LIBRARY_ACTIONS.SHARE_LIBRARY_AJAX,
			AJAX:{
				url:`creative-libraries/${id}/share`,
				method:"post",
				data:{
					emails:withWho,
				},
				success:LIBRARY_ACTIONS.SHARE_LIBRARY_SUCCESS,
				error:LIBRARY_ACTIONS.SHARE_LIBRARY_ERROR,
			},
			meta:{
				tracking: {
					type:          "Library",
					eventCategory: "Share",
					eventLabel:    "Share Library",
				},
			},
		});
	};
}

export function loadAdvertisers(mediaBuyerId, { searchTerm = "", page = 1 } = {}, cancelToken){
	return (dispatch, getState) => {
		if(!mediaBuyerId){
			return Promise.reject({ success:false });
		}
		return dispatch({
			type: LIBRARY_ACTIONS.LOAD_LIBRARY_ADVERTISERS_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			payload: { page },
			AJAX: {
				gateway: "adm",
				url: `ui/media-buyers/${mediaBuyerId}/advertisers`,
				params: {
					rpp: DEFAULT_RPP,
					page,
					sortBy: "name",
					sortOrder: "asc",
					...(searchTerm && { searchTerm }),
				},
				success: LIBRARY_ACTIONS.LOAD_LIBRARY_ADVERTISERS_SUCCESS,
				error: LIBRARY_ACTIONS.LOAD_LIBRARY_ADVERTISERS_ERROR,
				cancelToken,
			},
		});
	};
}

export function resetForm(){
	return (dispatch, getState) => {
		return dispatch({
			type: LIBRARY_ACTIONS.CLEAR_PROPERTIES_FORM,
		});
	};
}

const initialLibraryPropertiesState = {
	success: true,
	isLoaded: true,
	hasErrorLoading: false,

	// Initial value for this field
	contentLimitInKilobytes:300,
	email:null,
	error:null,

	advertisers: [],
	isAdvertisersLoaded: true,
	hasErrorLoadingAdvertisers: false,
	advertisersPage: 1,
	advertisersTotalPages: 1,
};

const handlers = {
	[LIBRARY_ACTIONS.CLEAR_PROPERTIES_FORM]: clearForm,

	[LIBRARY_ACTIONS.CREATE_LIBRARY_AJAX]: isLoadingLibrary,
	[LIBRARY_ACTIONS.CREATE_LIBRARY_SUCCESS]: initLibrary,
	[LIBRARY_ACTIONS.CREATE_LIBRARY_ERROR]: initErrorLibrary,

	[LIBRARY_ACTIONS.UPDATE_LIBRARY_AJAX]: isLoadingLibrary,
	[LIBRARY_ACTIONS.UPDATE_LIBRARY_SUCCESS]: initLibrary,
	[LIBRARY_ACTIONS.UPDATE_LIBRARY_ERROR]: initErrorLibrary,

	[LIBRARY_ACTIONS.DELETE_LIBRARY_AJAX]: isDeletingLibrary,
	[LIBRARY_ACTIONS.DELETE_LIBRARY_SUCCESS]: deleteSuccess,
	[LIBRARY_ACTIONS.DELETE_LIBRARY_ERROR]: deleteFail,

	[LIBRARY_ACTIONS.LOAD_LIBRARY_AJAX]: isLoadingLibrary,
	[LIBRARY_ACTIONS.LOAD_LIBRARY_SUCCESS]: initLibrary,
	[LIBRARY_ACTIONS.LOAD_LIBRARY_ERROR]: initErrorLibrary,
	[LIBRARY_ACTIONS.LOAD_LIBRARY_CANCELED]: cancelLibrary,

	[LIBRARY_ACTIONS.SHARE_LIBRARY_AJAX]:    initSharing,
	[LIBRARY_ACTIONS.SHARE_LIBRARY_ERROR]:   doneSharing,
	[LIBRARY_ACTIONS.SHARE_LIBRARY_SUCCESS]: doneSharing,

	[LIBRARY_ACTIONS.LOAD_LIBRARY_ADVERTISERS_AJAX]: isLoadingLibraryAdvertisers,
	[LIBRARY_ACTIONS.LOAD_LIBRARY_ADVERTISERS_SUCCESS]: initLibraryAdvertisers,
	[LIBRARY_ACTIONS.LOAD_LIBRARY_ADVERTISERS_ERROR]: initErrorLibraryAdvertisers,
};

function initSharing(state, action){
	return { ...state, isSharingLibrary:true };
}
function doneSharing(state, action){
	return { ...state, isSharingLibrary:false };
}

function clearForm(state, action){
	return { ...initialLibraryPropertiesState };
}

function isLoadingLibrary(state, action){
	return {
		...state,
		isLoaded: false,
		error:null,

		advertisers: [],
		isAdvertisersLoaded: true, // If we change this to bundle an advertiser fetch with a library detail fetch, we have to change this to false.
	};
}

function initLibrary(state, action){
	let { data } = action.payload;

	return {
		...state,
		...data,
		isLoaded: true,
		hasErrorLoading: false,
		error:null,
	};
}

function initErrorLibrary(state, action){
	return {
		...state,
		isLoaded: true,
		hasErrorLoading: true,
		error:{
			...action.payload.errorObj,
		},
	};
}

function cancelLibrary(state, action){
	return {
		...state,
		isLoaded: state?.id !== undefined,
		hasErrorLoading: false,
		error:null,
	};
}


function isDeletingLibrary(state, action){
	return {
		...state,
		error:null,
		isDeleting:true,
	};
}

function deleteSuccess(state, action){
	return {
		...state,
		error:null,
		isDeleting:false,
	};
}

function deleteFail(state, action){
	return {
		...state,
		isDeleting:false,
		error:{
			message:action.payload.errorObj.description,
		},
		hasErrorLoading: true,
	};
}

function isLoadingLibraryAdvertisers(state, action){
	const isNewSearch = action.payload?.original?.page === 1;
	return {
		...state,
		advertisers: isNewSearch ? [] : state.advertisers,
		isAdvertisersLoaded: false,
		hasErrorLoadingAdvertisers: false,
	};
}

function initLibraryAdvertisers(state, action){
	let { items, totalPages } = action.payload.data;
	let page = action.payload.original?.page ?? 1;

	return {
		...state,
		advertisers: page > 1 ? [...state.advertisers, ...items] : items,
		advertisersPage: page,
		advertisersTotalPages: totalPages,
		isAdvertisersLoaded: true,
		hasErrorLoadingAdvertisers: false,
	};
}

function initErrorLibraryAdvertisers(state, action){
	return {
		...state,
		isAdvertisersLoaded: true,
		hasErrorLoadingAdvertisers: true,
		error:{
			...action.payload.errorObj,
		},
	};
}

const buildLibraryDeleteResultMessage = (response, isSuccess) => {
	const messageId = new Date().getTime();
	if (isSuccess) {
		return {
			type: "success",
			id: messageId,
			messageHeader: "Library deleted successfully",
		};
	} else {
		return {
			type: "error",
			id: messageId,
			messageHeader: "Unable to delete Library",
			message: response?.data?.description || "An error occurred while deleting the library",
		};
	}
};

export default createReducer(initialLibraryPropertiesState, handlers);
export const HAND = handlers;
